import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Radar, MessageSquare, Calendar, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/** Chair Attention cockpit — 3 things needing action + upcoming meeting + 90-day heatmap.
 *  Composes from existing endpoints (frontend-only aggregation). See PRD backlog for
 *  a future `/chair-cockpit` aggregate endpoint to collapse the 4 round-trips. */
export default function ChairAttention({ schemeId }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!schemeId) return;
    let mounted = true;
    (async () => {
      try {
        const [levies, compliance, meetings, threads] = await Promise.all([
          api.get(`/schemes/${schemeId}/levies`),
          api.get(`/schemes/${schemeId}/compliance`),
          api.get(`/schemes/${schemeId}/meetings`),
          api.get(`/schemes/${schemeId}/messaging/threads`).catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        const overdueLevies = (levies.data || []).filter((l) => l.status === "overdue");
        const arrearsTotal = overdueLevies.reduce((s, l) => s + (l.total_amount || 0), 0);

        const now = new Date();
        const in30d = new Date(now.getTime() + 30 * 86400_000);
        const in90d = new Date(now.getTime() + 90 * 86400_000);
        const compExpiring = (compliance.data || []).filter((c) => {
          const d = c.due_date && new Date(c.due_date);
          return d && d >= now && d <= in30d && c.status !== "completed" && c.status !== "dismissed";
        });
        const earliestDue = compExpiring.map((c) => c.due_date).sort()[0];

        // Heatmap: 13 weeks × count of upcoming compliance items per week
        const weeks = Array.from({ length: 13 }, (_, i) => {
          const start = new Date(now.getTime() + i * 7 * 86400_000);
          const end = new Date(start.getTime() + 7 * 86400_000);
          const items = (compliance.data || []).filter((c) => {
            const d = c.due_date && new Date(c.due_date);
            return d && d >= start && d < end && c.status !== "completed" && c.status !== "dismissed";
          });
          return { start, count: items.length };
        });

        const upcoming = (meetings.data || [])
          .filter((m) => ["notice_sent", "draft", "in_progress"].includes(m.status))
          .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""))[0];

        const unresponded = (threads.data || []).filter((t) => t.status === "OPEN" || t.status === "UNANSWERED").length;
        const oldestWaitDays = (threads.data || [])
          .filter((t) => t.status === "OPEN" || t.status === "UNANSWERED")
          .map((t) => Math.floor((now.getTime() - new Date(t.last_message_at || t.created_at).getTime()) / 86400_000))
          .sort((a, b) => b - a)[0] || 0;

        setState({
          loading: false,
          overdueLevies: overdueLevies.length,
          arrearsTotal,
          compExpiringCount: compExpiring.length,
          compEarliest: earliestDue,
          unresponded,
          oldestWaitDays,
          weeks,
          upcoming,
          scheduledInRange: (compliance.data || []).filter((c) => {
            const d = c.due_date && new Date(c.due_date);
            return d && d >= now && d <= in90d;
          }).length,
        });
      } catch (_e) {
        if (mounted) setState({ loading: false, err: true });
      }
    })();
    return () => { mounted = false; };
  }, [schemeId]);

  if (state.loading) return <div className="mb-6 h-40 rounded-xl border animate-pulse bg-slate-50" style={{ borderColor: "var(--border)" }} />;
  if (state.err) return null;

  const cards = [
    { key: "arrears", icon: AlertTriangle, tone: state.overdueLevies > 0 ? "alert" : "ok",
      label: "Overdue levies",
      value: state.overdueLevies,
      sub: state.overdueLevies > 0 ? `$${state.arrearsTotal.toLocaleString()} outstanding` : "All levies current",
      cta: { label: "Review arrears", to: "/scheme/levies" }, testid: "chair-attention-arrears" },
    { key: "compliance", icon: Radar, tone: state.compExpiringCount > 0 ? "warn" : "ok",
      label: "Compliance in ≤30 days",
      value: state.compExpiringCount,
      sub: state.compEarliest ? `Earliest: ${new Date(state.compEarliest).toLocaleDateString()}` : "Nothing on the radar",
      cta: { label: "Review compliance", to: "/scheme/compliance" }, testid: "chair-attention-compliance" },
    { key: "messages", icon: MessageSquare, tone: state.unresponded > 0 ? "warn" : "ok",
      label: "Unresponded owner messages",
      value: state.unresponded,
      sub: state.oldestWaitDays > 0 ? `Oldest: ${state.oldestWaitDays}d ago` : "Inbox at zero",
      cta: { label: "Open messages", to: "/scheme/messages" }, testid: "chair-attention-messages" },
  ];

  const toneStyle = { alert: { c: "var(--alert)", bg: "#fee2e2" }, warn: { c: "#92400e", bg: "#fef3c7" }, ok: { c: "#166534", bg: "#dcfce7" } };

  return (
    <div className="mb-8" data-testid="chair-attention-cockpit">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>Things needing your attention today</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const st = toneStyle[c.tone];
          return (
            <div key={c.key} className="rounded-xl border bg-white p-5 flex flex-col" style={{ borderColor: "var(--border)" }} data-testid={c.testid}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: st.bg }}>
                  <Icon className="w-4 h-4" style={{ color: st.c }} />
                </div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.label}</div>
              </div>
              <div className="mt-3 text-4xl font-bold tabular-nums" style={{ color: c.tone === "alert" ? "var(--alert)" : "var(--navy)" }}>{c.value}</div>
              <div className="mt-1 text-sm text-slate-600">{c.sub}</div>
              <Link to={c.cta.to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--blue)" }}>
                {c.cta.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          );
        })}
      </div>

      {state.upcoming && (
        <div className="mt-6 rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="chair-upcoming-meeting">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <Calendar className="w-3.5 h-3.5" /> Upcoming meeting
          </div>
          <div className="mt-2 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-base font-semibold" style={{ color: "var(--navy)" }}>{state.upcoming.title}</div>
              <div className="text-sm text-slate-500">
                {state.upcoming.meeting_type} · {state.upcoming.scheduled_at ? new Date(state.upcoming.scheduled_at).toLocaleString() : "date TBD"} · status: {state.upcoming.status}
              </div>
            </div>
            <Link to={`/scheme/meetings/${state.upcoming.meeting_id}`}>
              <span className="text-sm font-semibold" style={{ color: "var(--blue)" }}>Open meeting →</span>
            </Link>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="chair-compliance-heatmap">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
          <Radar className="w-3.5 h-3.5" /> Next 90 days · compliance heatmap
        </div>
        <div className="flex items-end gap-1.5">
          {state.weeks.map((w, i) => {
            const bg = w.count >= 2 ? "var(--alert)" : w.count === 1 ? "#f59e0b" : "#e2e8f0";
            const height = 24 + (w.count * 12);
            return (
              <Link
                key={i}
                to={`/scheme/compliance?week=${i}`}
                className="flex-1 rounded-md group relative"
                style={{ height, background: bg, minWidth: 12 }}
                title={`Week of ${w.start.toLocaleDateString()} — ${w.count} item(s)`}
              />
            );
          })}
        </div>
        <div className="mt-2 text-[11px] text-slate-500 flex justify-between">
          <span>Now</span><span>+90d · {state.scheduledInRange} items in window</span>
        </div>
      </div>
    </div>
  );
}
